/**
 * Line diff between two versions of a pasta, rendered as unified text so the
 * `diff` grammar already in the language list can highlight it. No dependency:
 * a line-level LCS is a few dozen lines and pulling in a diff library to show
 * what changed in a 200-line script would be the larger cost.
 */

type Sign = ' ' | '-' | '+'

interface Op {
  sign: Sign
  text: string
}

/**
 * The LCS table is a cell per line pair. Scripts diff in milliseconds; a pair
 * of 512 KB pastas that share no common prefix would not, so past this many
 * cells the whole block is reported as replaced instead of hanging the tab.
 */
const MAX_CELLS = 4_000_000

/** Lines of unchanged context kept either side of a change. */
const DEFAULT_CONTEXT = 3

export function unifiedDiff(
  before: string,
  after: string,
  labels: { before: string; after: string },
  context = DEFAULT_CONTEXT,
): string {
  const hunks = toHunks(diffOps(toLines(before), toLines(after)), context)
  if (!hunks.length) return ''
  return [`--- ${labels.before}`, `+++ ${labels.after}`, ...hunks].join('\n')
}

/**
 * A trailing newline terminates the last line rather than starting an empty
 * one, and an empty pasta has no lines at all. Getting this wrong shifts every
 * line number in the header by one.
 */
function toLines(text: string): string[] {
  if (!text) return []
  const lines = text.split('\n')
  if (lines.at(-1) === '') lines.pop()
  return lines
}

function diffOps(a: string[], b: string[]): Op[] {
  // Edits are usually local, and trimming the matching ends first is what
  // keeps the table small enough to be worth building at all.
  let start = 0
  while (start < a.length && start < b.length && a[start] === b[start]) start++

  let endA = a.length
  let endB = b.length
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--
    endB--
  }

  return [
    ...a.slice(0, start).map(keep),
    ...middle(a.slice(start, endA), b.slice(start, endB)),
    ...a.slice(endA).map(keep),
  ]
}

const keep = (text: string): Op => ({ sign: ' ', text })
const drop = (text: string): Op => ({ sign: '-', text })
const add = (text: string): Op => ({ sign: '+', text })

function middle(a: string[], b: string[]): Op[] {
  if (!a.length) return b.map(add)
  if (!b.length) return a.map(drop)
  if (a.length * b.length > MAX_CELLS) return [...a.map(drop), ...b.map(add)]

  const width = b.length + 1
  // table[i][j] = length of the longest common subsequence of a[i:] and b[j:].
  const table = new Uint32Array((a.length + 1) * width)
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      table[i * width + j] =
        a[i] === b[j]
          ? (table[(i + 1) * width + (j + 1)] ?? 0) + 1
          : Math.max(table[(i + 1) * width + j] ?? 0, table[i * width + (j + 1)] ?? 0)
    }
  }

  const ops: Op[] = []
  let i = 0
  let j = 0
  while (i < a.length && j < b.length) {
    const left = a[i] ?? ''
    const right = b[j] ?? ''
    if (left === right) {
      ops.push(keep(left))
      i++
      j++
    } else if ((table[(i + 1) * width + j] ?? 0) >= (table[i * width + (j + 1)] ?? 0)) {
      ops.push(drop(left))
      i++
    } else {
      ops.push(add(right))
      j++
    }
  }
  while (i < a.length) ops.push(drop(a[i++] ?? ''))
  while (j < b.length) ops.push(add(b[j++] ?? ''))
  return ops
}

/** Groups the changes into `@@` hunks, merging any that share context. */
function toHunks(ops: Op[], contextLines: number): string[] {
  const changed = ops.flatMap((op, index) => (op.sign === ' ' ? [] : [index]))
  if (!changed.length) return []

  const ranges: { from: number; to: number }[] = []
  for (const index of changed) {
    const from = Math.max(0, index - contextLines)
    const to = Math.min(ops.length - 1, index + contextLines)
    const last = ranges.at(-1)
    // Touching or overlapping windows read better as one hunk than as two.
    if (last && from <= last.to + 1) last.to = to
    else ranges.push({ from, to })
  }

  // Line numbers as each side counts them: a removal doesn't advance the new
  // file, an addition doesn't advance the old one.
  const lineA: number[] = []
  const lineB: number[] = []
  let a = 1
  let b = 1
  for (const op of ops) {
    lineA.push(a)
    lineB.push(b)
    if (op.sign !== '+') a++
    if (op.sign !== '-') b++
  }

  return ranges.map(({ from, to }) => {
    const slice = ops.slice(from, to + 1)
    const countA = slice.filter((op) => op.sign !== '+').length
    const countB = slice.filter((op) => op.sign !== '-').length
    const startA = countA ? (lineA[from] ?? 1) : 0
    const startB = countB ? (lineB[from] ?? 1) : 0
    return [
      `@@ -${startA},${countA} +${startB},${countB} @@`,
      ...slice.map((op) => op.sign + op.text),
    ].join('\n')
  })
}
